/* ==========================================================================
   Free gift with purchase

   Keeps the cart's gift lines in agreement with a set of rules: buying a
   trigger product puts its gift in the cart, removing the trigger takes the
   gift back out, and the gift's quantity is never something the shopper chose.

   The cart is the only source of truth. Nothing is remembered in localStorage,
   because a cart can be changed from another tab, from the cart page, from the
   drawer, or by the back button, and any cached idea of "already added" would
   drift out of step with it.

   Every pass is a full reconcile rather than an incremental patch: read the
   cart, work out what the gift lines should be, and apply the difference. That
   makes a missed event or a failed request self-correcting on the next pass
   instead of leaving the cart permanently wrong.
   ========================================================================== */

(() => {
  const config = window.__giftRules;
  if (!config || !config.rules || !config.rules.length) return;
  const inEditor = !!(window.Shopify && window.Shopify.designMode);

  // A rule can be earned by more than one product, and every trigger's quantity
  // counts toward the same gift. Older markup sends a single triggerProductId.
  config.rules.forEach((rule) => {
    if (!Array.isArray(rule.triggerProductIds) || !rule.triggerProductIds.length) {
      rule.triggerProductIds = [rule.triggerProductId];
    }
    rule.triggerProductIds = rule.triggerProductIds.map(Number);
  });

  const isTrigger = (rule, productId) => rule.triggerProductIds.includes(Number(productId));

  // How many of a rule's trigger products the given cart lines hold, together.
  const triggerCount = (rule, items) => items
    .filter((i) => isTrigger(rule, i.product_id))
    .reduce((sum, i) => sum + i.quantity, 0);

  // The rule an add request is buying into: by variant id when the product is
  // not in the cart yet, by the cart line's product when it already is.
  const ruleForVariant = (variantId, cart) => config.rules.find((r) =>
    (r.triggerVariantIds || []).includes(variantId) ||
    cart.items.some((i) => i.variant_id === variantId && isTrigger(r, i.product_id)));

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const CART_PATHS = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const GIFT_PROP = '_gift';

  const giftVariants = new Set(config.rules.map((r) => r.giftVariantId));

  /* ------------------------------------------------------------ plumbing -- */

  // Bundled section rendering: asking the cart endpoint itself for the sections
  // means the markup is rendered after the change, in the same round trip, so
  // there is no window where the response and the page disagree.
  const withSections = (body) => {
    const ids = sectionIdsToFetch();
    return ids.length ? { ...body, sections: ids.join(',') } : body;
  };

  const json = async (path, body) => {
    for (let attempt = 0; attempt < 3; attempt++) {
      const res = await fetch(ROOT + path, {
        method: body ? 'POST' : 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      // The cart endpoints serialise per session; a burst of updates can come
      // back throttled rather than applied.
      if (res.status === 429) {
        await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
        continue;
      }
      if (!res.ok) throw new Error(`${path} -> ${res.status}`);
      return res.json();
    }
    throw new Error(`${path} -> throttled`);
  };

  /* ------------------------------------------------------------- reconcile */

  // What the gift lines ought to look like, given what else is in the cart.
  function desiredGifts(cart) {
    const counts = new Map();
    cart.items.forEach((item) => {
      counts.set(item.variant_id, (counts.get(item.variant_id) || 0) + item.quantity);
      counts.set('p' + item.product_id, (counts.get('p' + item.product_id) || 0) + item.quantity);
    });

    const want = new Map();
    config.rules.forEach((rule) => {
      const bought = rule.triggerProductIds.reduce((sum, id) => sum + (counts.get('p' + id) || 0), 0);
      if (bought <= 0) return;
      const qty = rule.mode === 'per_item' ? Math.min(bought, rule.max) : 1;
      want.set(rule.giftVariantId, Math.max(want.get(rule.giftVariantId) || 0, qty));
    });
    return want;
  }

  function plan(cart) {
    const want = desiredGifts(cart);
    const changes = [];

    // Grouped by variant, never judged line by line. Shopify splits a line the
    // moment a discount covers only part of it, so one gift can arrive as two
    // lines of one — and comparing each of those against a target of one would
    // call both correct and quietly leave twice the gift in the cart.
    const linesByVariant = new Map();
    cart.items.forEach((item) => {
      if (!giftVariants.has(item.variant_id)) return;
      if (!linesByVariant.has(item.variant_id)) linesByVariant.set(item.variant_id, []);
      linesByVariant.get(item.variant_id).push(item);
    });

    linesByVariant.forEach((lines, variantId) => {
      const target = want.get(variantId) || 0;
      // The whole amount goes on the first line and the rest are emptied, which
      // also collapses a split back into one line.
      lines.forEach((line, index) => {
        const lineTarget = index === 0 ? target : 0;
        if (line.quantity !== lineTarget) {
          changes.push({ type: 'set', key: line.key, quantity: lineTarget });
        }
      });
    });

    want.forEach((qty, variantId) => {
      if (!linesByVariant.has(variantId)) changes.push({ type: 'add', variantId, quantity: qty });
    });

    return changes;
  }

  /* ------------------------------------------------------------- applying -- */

  let running = false;
  let rerun = false;
  let internal = false;
  let settleTimer = null;

  // Short enough that the shopper does not watch a wrong quantity sit there,
  // long enough to coalesce a burst of clicks on the quantity stepper into one
  // pass. Losing the race with the theme's own paint no longer matters, because
  // refreshCartUI paints a second time after it settles.
  const SETTLE_MS = 80;

  function scheduleReconcile() {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(reconcile, SETTLE_MS);
  }

  async function reconcile() {
    if (running) { rerun = true; return; }
    running = true;
    try {
      const cart = await json('cart.js');
      const changes = plan(cart);
      if (!changes.length) {
        if (!markupMatchesCart(cart)) await refreshCartUI();
        settle();
        return;
      }

      const last = await applyGiftChanges(changes, sectionIdsToFetch());
      if (last && last.sections) applySections(last.sections, last.item_count);
      else await refreshCartUI();
      if (last && typeof last.item_count === 'number') closeDrawerIfEmptied(last);
      settle();
    } catch (error) {
      // A cart that cannot be reconciled must still be usable, so this never
      // interrupts the shopper. The next cart event tries again.
      console.warn('[gift] reconcile skipped:', error.message);
    } finally {
      internal = false;
      running = false;
      if (rerun) { rerun = false; reconcile(); }
    }
  }

  /* -------------------------------------------------------------- repaint -- */

  // Painted EXACTLY the way the decoded theme paints itself: the same section,
  // the same target, the same wholesale innerHTML replacement of .drawer__inner.
  // Mixing finer-grained child swaps with the theme's wholesale replacement
  // interleaves two generations of markup — which is what briefly doubled the
  // belt row — and a child selector the theme does not share (its heading is
  // .drawer__heading) simply never updates, which is what froze the count.
  const MAX_SECTIONS = 5;

  function paintTargets() {
    const t = [];
    if (document.getElementById('CartDrawer')) {
      t.push({ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' });
    }
    if (document.getElementById('cart-icon-bubble')) {
      t.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    }
    const items = document.getElementById('main-cart-items');
    if (items && items.dataset.id) t.push({ id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' });
    const footer = document.getElementById('main-cart-footer');
    if (footer && footer.dataset.id) t.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
    return t;
  }

  function sectionIdsToFetch() {
    return [...new Set(paintTargets().map((t) => t.section))].slice(0, MAX_SECTIONS);
  }

  // The badge carries the count, so the stepper can be hidden without the
  // shopper losing sight of how many gifts they have.
  function labelGiftQuantities() {
    config.rules.forEach((rule) => {
      if (!rule.giftHandle) return;
      document.querySelectorAll('.cart-item--product-' + rule.giftHandle).forEach((line) => {
        const input = line.querySelector('.quantity__input, input[name="updates[]"]');
        const qty = input ? Number(input.value) : 0;
        line.querySelectorAll('.cart-item__quantity').forEach((cell) => {
          cell.setAttribute('data-gift-qty', qty > 0 ? '×' + qty : '');
        });
      });
    });
  }

  function settle() {
    labelGiftQuantities();
    syncEmptyState();
  }

  function applySections(sections, itemCount) {
    if (!sections) return;
    paintTargets().forEach((t) => {
      // A section that fails to render comes back as null instead of failing
      // the request, so each one is checked on its own.
      const html = sections[t.section];
      if (typeof html !== 'string') return;
      const host = document.getElementById(t.id);
      if (!host) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(t.selector);
      if (!parsed) return;
      const target = host.querySelector(t.selector) || host;
      target.innerHTML = parsed.innerHTML;
    });
    // The theme toggles these from item_count in its own handler; painting its
    // markup without its class toggle would strand the empty state.
    if (typeof itemCount === 'number') {
      document.querySelectorAll('cart-drawer, #main-cart-footer').forEach((el) => {
        el.classList.toggle('is-empty', itemCount === 0);
      });
    }
    settle();
  }

  async function paint() {
    const targets = paintTargets();
    if (!targets.length) return;
    const csv = [...new Set(targets.map((t) => t.section))].slice(0, MAX_SECTIONS).join(',');
    const res = await fetch(`${ROOT}?sections=${csv}`, { headers: { Accept: 'application/json' } });
    const known = cartCache;
    applySections(await res.json(), known && typeof known.item_count === 'number' ? known.item_count : undefined);
  }

  // Shopify orders cart lines by when they were created, and there is no way to
  // reorder them without removing and re-adding — which would churn the cart on
  // every pass. Placing the gift under its trigger is presentation, so it is
  // done in the markup after each paint instead.
  // `is-empty` sits on <cart-drawer>, which the surgical swap never replaces,
  // so removing the last line would otherwise leave the drawer looking full.
  // Folding the gift into the shopper's own add leaves nothing for the pass to
  // change, so it would never repaint and would be relying on the theme having
  // done so. Comparing what is on screen with what is in the cart makes that
  // explicit instead of assumed, and costs one request only when they disagree.
  function markupMatchesCart(cart) {
    const list = document.querySelector('#CartDrawer-CartItems, #main-cart-items');
    if (!list) return true;
    return list.querySelectorAll('.cart-item').length === cart.items.length;
  }

  function syncEmptyState() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    const hasLines = !!document.querySelector('#CartDrawer-CartItems .cart-item');
    drawer.classList.toggle('is-empty', !hasLines);
  }


  // The theme repaints the cart on its own whenever the shopper changes a
  // quantity, and that repaint restores Shopify's line order. Watching the DOM
  // catches those repaints too, not only the ones this file triggers.
  const watchCartMarkup = () => {
    const giftSelector = config.rules
      .filter((r) => r.giftHandle)
      .map((r) => '.cart-item--product-' + r.giftHandle)
      .join(',');
    if (!giftSelector) return;

    let queued = false;
    const observer = new MutationObserver(() => {
      // Coalesced to one run per frame, and gated on a single cheap query:
      // the page mutates constantly for reasons that have nothing to do with
      // the cart, and reacting to each record is what turns this expensive.
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        if (!document.querySelector(giftSelector)) return;
        settle();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  async function refreshCartUI() {
    try {
      await paint();
      // The theme paints from its own response, which was taken before the gift
      // lines were corrected. Painting again once it has settled makes ours the
      // last word without having to guess how long its request takes.
      setTimeout(() => paint().catch(() => {}), 400);
    } catch {
      /* The cart itself is already correct; only its rendering is stale. */
    }
    document.dispatchEvent(new CustomEvent('gift:cart-changed'));
  }

  /* --------------------------------------------------------- interception -- */

  /* Correcting the cart after the theme has already written to it always shows
     a wrong state first: the shopper sees four gifts against five belts, and
     Shopify splits the belt line because only four of them are paired. The fix
     is to get in front of the write instead of chasing it.

     For an add, the gift is folded into the same request, so one round trip
     puts both lines in the cart and the drawer opens already correct.

     For a quantity change, the gift is corrected first and the theme's own
     request is sent afterwards — so the response the theme paints from is
     already the finished cart, and no intermediate state is ever rendered. */

  let cartCache = null;
  let cartCachedAt = 0;
  const CACHE_TTL = 8000;

  const cartUrl = (u) => String(u && u.url ? u.url : u || '');
  const bodyOf = (init) => {
    try {
      if (!init || !init.body || typeof init.body !== 'string') return null;
      return JSON.parse(init.body);
    } catch { return null; }
  };

  async function snapshot() {
    if (cartCache && Date.now() - cartCachedAt < CACHE_TTL) return cartCache;
    cartCache = await json('cart.js');
    cartCachedAt = Date.now();
    return cartCache;
  }

  // Every cart endpoint answers with the cart, so the snapshot can be kept warm
  // for free. Without this, the first add on a page pays for a cart lookup
  // before it can fold the gift in, and the shopper watches it arrive late.
  function rememberCartData(data) {
    if (data && data.token && typeof data.item_count === 'number' && Array.isArray(data.items)) {
      cartCache = data;
      cartCachedAt = Date.now();
    }
  }

  function rememberCart(res) {
    try {
      res.clone().json().then((data) => {
        // add.js answers with only the lines it touched; taking that for the
        // whole cart would poison every later decision. A full cart always
        // carries a token and an item_count.
        if (data && data.token && typeof data.item_count === 'number' && Array.isArray(data.items)) {
          cartCache = data; cartCachedAt = Date.now();
        }
      }).catch(() => {});
    } catch { /* opaque or already-consumed responses are simply not cached */ }
  }

  // How many gifts the cart should hold once the trigger reaches `bought`.
  function giftsFor(rule, bought) {
    if (bought <= 0) return 0;
    return rule.mode === 'per_item' ? Math.min(bought, rule.max) : 1;
  }

  function ruleForProduct(productId) {
    return config.rules.find((r) => isTrigger(r, productId));
  }

  async function augmentAdd(payload) {
    const items = payload.items || [payload];
    const cart = await snapshot();

    // Totalled per rule before anything is decided: one request can carry both
    // of a rule's triggers, and judging them one at a time would count each
    // against the cart as it was, under-counting the gifts they earn together.
    const addedByRule = new Map();
    for (const item of items) {
      const rule = ruleForVariant(Number(item.id), cart);
      if (!rule) continue;
      addedByRule.set(rule, (addedByRule.get(rule) || 0) + (Number(item.quantity) || 1));
    }

    const extra = [];
    addedByRule.forEach((added, rule) => {
      const giftHave = cart.items
        .filter((i) => i.variant_id === rule.giftVariantId)
        .reduce((a, i) => a + i.quantity, 0);
      const need = giftsFor(rule, triggerCount(rule, cart.items) + added) - giftHave;
      if (need > 0) {
        extra.push({ id: rule.giftVariantId, quantity: need, properties: { [GIFT_PROP]: 'true' } });
      }
    });

    if (!extra.length) return null;
    return { ...payload, items: [...items, ...extra] };
  }

  // Deleting the last paid line leaves an open drawer showing nothing: the
  // gifts leave with their trigger, and an empty drawer left open reads as a
  // bug rather than as a finished action.
  function closeDrawerIfEmptied(data) {
    if (!data || data.item_count !== 0) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !drawer.classList.contains('active')) return;
    setTimeout(() => {
      try {
        if (typeof drawer.close === 'function') drawer.close();
        else {
          const overlay = drawer.querySelector('.cart-drawer__overlay, #CartDrawer-Overlay');
          if (overlay) overlay.click();
          else drawer.classList.remove('active');
        }
      } catch {
        drawer.classList.remove('active');
        document.body.classList.remove('overflow-hidden');
      }
      // The scroll lock is deliberately left to the theme's own close(): lifting
      // it while the panel is still sliding out lets the page scroll sideways to
      // the half-off-screen drawer, which is the flash of a horizontal scrollbar.
    }, 250);
  }

  function watchForEmpty(res) {
    try {
      res.clone().json().then((d) => {
        if (d && typeof d.item_count === 'number') closeDrawerIfEmptied(d);
      }).catch(() => {});
    } catch { /* not json */ }
  }

  /* --------------------------------------------------------------- events -- */

  // The theme's cart code is obfuscated and hosted remotely, so this sits on
  // the network instead of hooking its internals.
  //
  // change.js is deliberately left untouched. The decoded theme both ADDRESSES
  // a line by number and VERIFIES its response by that same number — it reads
  // items[line-1].quantity back and reverts the input on any mismatch — so no
  // write of ours may move the cart between its render and its request. The
  // gift catches up immediately after, addressed by stable key, and the
  // bundled repaint refreshes every data-index before the next click.
  //
  // update.js carries the whole cart in one positional body, so there the
  // gift's quantity is rewritten INSIDE the same request instead.
  const nativeFetch = window.fetch;

  function parseCartBody(init) {
    const raw = init && init.body;
    if (!raw) return null;
    if (typeof raw === 'string') {
      try { return { kind: 'json', data: JSON.parse(raw) }; } catch { /* not json */ }
      try {
        const p = new URLSearchParams(raw);
        if ([...p.keys()].length) return { kind: 'urlencoded', data: p };
      } catch { /* not urlencoded either */ }
      return null;
    }
    if (typeof FormData !== 'undefined' && raw instanceof FormData) return { kind: 'form', data: raw };
    if (raw instanceof URLSearchParams) return { kind: 'urlencoded', data: raw };
    return null;
  }

  function updatesOf(parsed) {
    if (!parsed) return null;
    if (parsed.kind === 'json') return parsed.data.updates || null;
    const arr = parsed.data.getAll('updates[]');
    if (arr.length) return arr.map(Number);
    const obj = {};
    let found = false;
    for (const [k, v] of parsed.data.entries()) {
      const m = /^updates\[(.+)\]$/.exec(k);
      if (m) { obj[m[1]] = Number(v); found = true; }
    }
    return found ? obj : null;
  }

  function writeUpdatesBack(parsed, updates, init) {
    if (parsed.kind === 'json') {
      return { ...init, body: JSON.stringify({ ...parsed.data, updates }) };
    }
    const out = parsed.kind === 'form' ? new FormData() : new URLSearchParams();
    for (const [k, v] of parsed.data.entries()) {
      if (k === 'updates[]' || /^updates\[.+\]$/.test(k)) continue;
      out.append(k, v);
    }
    if (Array.isArray(updates)) updates.forEach((q) => out.append('updates[]', String(q)));
    else Object.keys(updates).forEach((k) => out.append('updates[' + k + ']', String(updates[k])));
    return { ...init, body: out };
  }

  // update.js carries the whole cart in one body — an array mapped to lines by
  // position, or an object keyed by variant — so the gift's correct quantity
  // can ride inside the same write. One request, one paint, and the positions
  // the theme computed stay valid because nothing changed before it.
  function rewriteUpdates(updates, cart) {
    const isArr = Array.isArray(updates);
    if (isArr && updates.length !== cart.items.length) return null;

    const counts = new Map();
    cart.items.forEach((it, i) => {
      let q = it.quantity;
      if (isArr) q = Number(updates[i]);
      else if (updates[it.variant_id] != null) q = Number(updates[it.variant_id]);
      else if (updates[it.key] != null) q = Number(updates[it.key]);
      counts.set(it.product_id, (counts.get(it.product_id) || 0) + q);
    });

    let touched = false;
    const out = isArr ? updates.slice() : { ...updates };
    config.rules.forEach((rule) => {
      const bought = rule.triggerProductIds.reduce((sum, id) => sum + (counts.get(id) || 0), 0);
      const want = giftsFor(rule, bought);
      if (isArr) {
        let first = true;
        cart.items.forEach((it, i) => {
          if (it.variant_id !== rule.giftVariantId) return;
          const target = first ? want : 0;
          first = false;
          if (Number(out[i]) !== target) { out[i] = target; touched = true; }
        });
      } else {
        const present = cart.items.some((it) => it.variant_id === rule.giftVariantId);
        if (present && Number(out[rule.giftVariantId]) !== want) {
          out[rule.giftVariantId] = want;
          touched = true;
        }
      }
    });
    return touched ? out : null;
  }

  async function interceptUpdate(input, init, rest) {
    try {
      const parsed = parseCartBody(init);
      const updates = updatesOf(parsed);
      if (updates) {
        const cart = await snapshot();
        const rewritten = rewriteUpdates(updates, cart);
        if (rewritten) init = writeUpdatesBack(parsed, rewritten, init);
      }
    } catch { /* the reconcile below repairs anything missed */ }
    const res = await nativeFetch.call(this, input, init, ...rest);
    rememberCart(res);
    watchForEmpty(res);
    scheduleReconcile();
    return res;
  }

  // change.js responses are held and finished before the theme reads them.
  // The write itself is never touched — but the moment between the theme's
  // write and the gift correction is exactly when Shopify's Buy-X-get-Y engine
  // splits partially-covered lines (a documented behaviour), and painting that
  // moment is the doubled row: plus splits the trigger, minus splits the gift.
  // Handing the theme the corrected cart means that moment is never rendered.
  async function interceptChange(input, init, rest) {
    const res = await nativeFetch.call(this, input, init, ...rest);
    let state = null;
    try { state = await res.clone().json(); } catch { /* not json */ }
    if (!res.ok || !state || state.errors || !state.token || !Array.isArray(state.items)) {
      if (state) rememberCartData(state);
      watchForEmpty(res);
      scheduleReconcile();
      return res;
    }
    rememberCartData(state);

    const changes = plan(state);
    // An add would need markup the held response cannot describe; that rare
    // case falls back to the reconcile instead.
    if (!changes.length || changes.some((c) => c.type === 'add')) {
      closeDrawerIfEmptied(state);
      scheduleReconcile();
      return res;
    }

    const parsed = parseCartBody(init);
    const wanted = [...new Set([
      ...themeSectionIds(null, parsed && parsed.kind === 'json' ? parsed.data : null),
      ...sectionIdsToFetch(),
    ])].slice(0, MAX_SECTIONS);

    let fin = null;
    try { fin = await applyGiftChanges(changes, wanted); } catch { /* fall back */ }
    scheduleReconcile();
    if (!fin || !fin.token || !Array.isArray(fin.items)) return res;
    rememberCartData(fin);
    closeDrawerIfEmptied(fin);

    // The theme verifies items[line-1] and paints from sections: both must
    // describe the finished cart, under the keys it asked for.
    const out = { ...fin };
    if (state.sections) {
      out.sections = { ...state.sections };
      if (fin.sections) {
        Object.keys(out.sections).forEach((k) => {
          if (typeof fin.sections[k] === 'string') out.sections[k] = fin.sections[k];
        });
      }
    }
    try {
      const headers = new Headers(res.headers || undefined);
      headers.delete('content-length');
      return new Response(JSON.stringify(out), { status: res.status, statusText: res.statusText || '', headers });
    } catch { return res; }
  }

  async function interceptJsonAdd(input, init, rest) {
    try {
      const payload = bodyOf(init);
      if (payload) {
        const merged = await augmentAdd(payload);
        if (merged) init = { ...init, body: JSON.stringify(merged) };
      }
    } catch { /* the reconcile below repairs anything missed */ }
    const res = await nativeFetch.call(this, input, init, ...rest);
    rememberCart(res);
    scheduleReconcile();
    return res;
  }

  // The product form posts FormData, which the JSON folding above cannot touch.
  // Rewriting it into add.js's items[] form lets the gift travel in the very
  // same request, so the discount is evaluated once with both lines present.
  // Nothing is ever momentarily unbalanced, so Shopify never splits the trigger
  // line and the markup the theme renders from is right the first time.
  async function foldGiftIntoForm(form) {
    if (typeof FormData === 'undefined' || !(form instanceof FormData)) return null;

    const variantId = Number(form.get('id'));
    if (!Number.isFinite(variantId) || !variantId) return null;
    const quantity = Number(form.get('quantity')) || 1;

    const cart = await snapshot();
    const rule = ruleForVariant(variantId, cart);
    if (!rule) return null;

    const already = triggerCount(rule, cart.items);
    const giftHave = cart.items
      .filter((i) => i.variant_id === rule.giftVariantId)
      .reduce((a, i) => a + i.quantity, 0);
    const need = giftsFor(rule, already + quantity) - giftHave;
    if (need <= 0) return null;

    const out = new FormData();
    for (const [key, value] of form.entries()) {
      if (key === 'id') out.append('items[0][id]', value);
      else if (key === 'quantity') out.append('items[0][quantity]', value);
      else if (key === 'selling_plan') out.append('items[0][selling_plan]', value);
      else if (/^properties\[/.test(key)) out.append('items[0][' + key + ']', value);
      else out.append(key, value);
    }
    out.append('items[1][id]', String(rule.giftVariantId));
    out.append('items[1][quantity]', String(need));
    out.append('items[1][properties][' + GIFT_PROP + ']', 'true');
    return out;
  }

  async function interceptFormAdd(input, init, rest) {
    const form = init.body;

    // Preferred route: one request carrying both lines.
    try {
      const folded = await foldGiftIntoForm(form);
      if (folded) {
        const one = await nativeFetch.call(this, input, { ...init, body: folded }, ...rest);
        if (one.ok) {
          const body = await one.clone().json().catch(() => null);
          // add.js reports a rejected item as a status code in the body rather
          // than a failed response, so both have to agree before it is trusted.
          if (body && !body.status) {
            scheduleReconcile();
            return one;
          }
        }
      }
    } catch { /* fall through and add exactly what the shopper asked for */ }

    const res = await nativeFetch.call(this, input, init, ...rest);
    if (!res.ok) { scheduleReconcile(); return res; }

    let data = null;
    try { data = await res.clone().json(); } catch { scheduleReconcile(); return res; }
    const added = Array.isArray(data.items) ? data.items : [data];

    let changes = [];
    try {
      // add.js answers with the added lines at their new totals, so the cart
      // after the write can be assembled locally instead of fetched.
      const before = await snapshot();
      const virtual = {
        items: before.items
          .filter((i) => !added.some((a) => a.key === i.key))
          .concat(added.map((a) => ({
            key: a.key, variant_id: a.variant_id, product_id: a.product_id,
            quantity: a.quantity, properties: a.properties || {},
          }))),
      };
      changes = plan(virtual);
    } catch { scheduleReconcile(); return res; }
    if (!changes.length) { scheduleReconcile(); return res; }

    const wanted = [...new Set([...themeSectionIds(form, null), ...sectionIdsToFetch()])].slice(0, MAX_SECTIONS);
    let last = null;
    try { last = await applyGiftChanges(changes, wanted); }
    catch { scheduleReconcile(); return res; }
    scheduleReconcile();
    if (!last || !last.sections) return res;

    // The page is updated here, and the markup inside the held response is
    // swapped for the post-gift render — so whichever of the two the theme
    // paints from, the drawer it opens already shows both lines.
    applySections(last.sections);
    if (data.sections) {
      Object.keys(data.sections).forEach((k) => {
        if (typeof last.sections[k] === 'string') data.sections[k] = last.sections[k];
      });
    }
    try {
      const headers = new Headers(res.headers || undefined);
      headers.delete('content-length');
      return new Response(JSON.stringify(data), { status: res.status, statusText: res.statusText || '', headers });
    } catch { return res; }
  }

  function themeSectionIds(form, body) {
    const raw = form ? form.get('sections') : body && body.sections;
    if (Array.isArray(raw)) return raw.filter(Boolean);
    return String(raw || '').split(',').map((x) => x.trim()).filter(Boolean);
  }

  // Applies gift changes and converges. Shopify's discount engine merges and
  // re-splits allocation lines underneath every write, so a line addressed by
  // key may vanish mid-sequence — each write is tolerant, and a second pass on
  // the fresh cart picks up whatever the merge moved. Sections are rendered
  // once at the end, from the settled cart.
  async function applyGiftChanges(changes, sectionIds) {
    const csv = (sectionIds || []).join(',');
    internal = true;
    try {
      let pending = changes;
      let now = null;
      for (let pass = 0; pass < 2 && pending.length; pass++) {
        for (const c of pending) {
          try {
            if (c.type === 'set') {
              await json('cart/change.js', { id: c.key, quantity: c.quantity });
            } else {
              await json('cart/add.js', {
                items: [{ id: c.variantId, quantity: c.quantity, properties: { [GIFT_PROP]: 'true' } }],
              });
            }
          } catch { /* the line may have merged away underneath us */ }
        }
        now = await json('cart.js');
        cartCache = now;
        cartCachedAt = Date.now();
        pending = plan(now);
      }
      if (!now) now = await json('cart.js');
      let sections = null;
      if (csv) {
        try {
          const res = await fetch(ROOT + '?sections=' + csv, { headers: { Accept: 'application/json' } });
          sections = await res.json();
        } catch { /* markup refresh is best-effort; the cart itself is settled */ }
      }
      return sections ? { ...now, sections } : { ...now };
    } finally {
      internal = false;
    }
  }

  window.fetch = function (input, init, ...rest) {
    const url = cartUrl(input);
    const isCartWrite = CART_PATHS.test(url);
    if (!isCartWrite || internal) {
      const out = nativeFetch.call(this, input, init, ...rest);
      if (isCartWrite) out.then(rememberCart).catch(() => {});
      return out;
    }
    if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
      if (init && typeof FormData !== 'undefined' && init.body instanceof FormData) {
        return interceptFormAdd.call(this, input, init, rest);
      }
      return interceptJsonAdd.call(this, input, init, rest);
    }
    if (/\/cart\/change(\.js)?(\?|$)/.test(url)) {
      return interceptChange.call(this, input, init, rest);
    }
    if (/\/cart\/update(\.js)?(\?|$)/.test(url)) {
      return interceptUpdate.call(this, input, init, rest);
    }
    return (async () => {
      const res = await nativeFetch.call(this, input, init, ...rest);
      rememberCart(res);
      watchForEmpty(res);
      scheduleReconcile();
      return res;
    })();
  };

  const nativeSend = XMLHttpRequest.prototype.send;
  const nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__giftCartWrite = CART_PATHS.test(String(url || ''));
    return nativeOpen.call(this, method, url, ...rest);
  };
  XMLHttpRequest.prototype.send = function (...args) {
    if (this.__giftCartWrite && !internal) {
      this.addEventListener('load', () => { cartCache = null; scheduleReconcile(); }, { once: true });
    }
    return nativeSend.apply(this, args);
  };

  // A non-AJAX add posts the form and reloads, so the pass on load covers it.
  const boot = () => {
    // Warmed straight away so the first add on the page can fold its gift in
    // without stopping to look the cart up first.
    snapshot().catch(() => {});
    settle();
    watchCartMarkup();
    reconcile();
  };

  // The editor re-renders a section's markup without re-running page scripts,
  // and emits these instead. Reacting to them is far cheaper than inferring the
  // same thing from DOM mutations, and it is what Shopify documents.
  document.addEventListener('shopify:section:load', settle);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
  const forget = () => { cartCache = null; };
  window.addEventListener('pageshow', (e) => { if (e.persisted) { forget(); reconcile(); } });
  // Coming back to a tab that sat idle, or that another tab has since changed.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') { forget(); reconcile(); }
  });

  window.__giftReconcile = reconcile;
  window.__giftForget = forget;
})();
